import type { ObjectId } from '@racemap/sdk/schema/base';
import { isNotEmptyString } from '@racemap/utilities/functions/utils';
import type { RacemapEvent } from '@racemap/utilities/types/events';
import type { Immutable } from 'immer';
import type { CurrentEvent } from '../store/events/events_reducers';

/**
 * Generates a player URL for viewing race events with optional query parameters.
 *
 * @param event - The race event object containing id and optional slug
 * @param options - Optional configuration object
 * @param options.starterIds - Optional list of starter IDs to pre-select multiple participants
 * @param options.time - Optional timestamp to jump to a specific time in the race
 *
 * @returns A complete URL string for the player page with optional hash parameters
 *
 * @example
 * ```typescript
 * // Basic usage
 * const url = getPlayerUrl(event);
 *
 * // With starter selection
 * const url = getPlayerUrl(event, { starterIds: ["123"] });
 *
 * // With time parameter
 * const url = getPlayerUrl(event, { time: new Date("2023-01-01T10:00:00Z") });
 *
 * // With both parameters
 * const url = getPlayerUrl(event, {
 *   starterIds: ["123"],
 *   time: new Date("2023-01-01T10:00:00Z")
 * });
 * ```
 */
export function getPlayerUrl(
  event: Immutable<RacemapEvent | CurrentEvent> | RacemapEvent | CurrentEvent | null,
  { starterIds, time }: { starterIds?: Array<string | ObjectId>; time?: Date } = {},
) {
  if (event == null) return '';
  const slug = isNotEmptyString(event.slug) ? event.slug : event.id;
  let url = `${window.location.origin}/player/${slug}`;

  const ids = (starterIds ?? [])
    .filter((id): id is string | ObjectId => id != null)
    .map((id) => id.toString())
    .filter((id) => id !== '');

  const params = new URLSearchParams();
  if (time != null) {
    params.append('time', time.toISOString());
  }
  if (ids.length > 0) {
    params.append('selected', ids.join(','));
  }
  const paramsString = params.toString();
  if (paramsString !== '') {
    url += `#${paramsString}`;
  }

  return url;
}
